import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import { cookieDomain } from "@/Environment";
import { useAuthentication } from "@/Slices/authenticationSlice";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import { useOnce } from "@/Hooks/useOnce";
import moment from "moment";
import { useState } from "react";
import { useCookies } from "react-cookie";
import { useUserService } from "@/Services/UserService";
import { DashboardUserSelect } from "@/Features/Dashboard/CustomComponents/DashboardUserSelect";
import type { BranchFilterOptionType } from "@/Shared/Types/BranchFilterOptionType";

export const UserSelectType = {
  Quoting: 0,
  Booking: 1,
} as const;
export type UserSelectType =
  (typeof UserSelectType)[keyof typeof UserSelectType];

export function useDashboardUserSelect(type: UserSelectType) {
  const { user } = useAuthentication();
  const [loading, setLoading] = useState(true);
  const [cookies, setCookie, removeCookie] = useCookies([
    "fsFilterBookedShipmentsByUserId",
    "fsFilterBookedShipmentsByBranchId",
  ]);

  const userCookie = cookies.fsFilterBookedShipmentsByUserId as
    | string
    | undefined;

  const branchCookie = cookies.fsFilterBookedShipmentsByBranchId as
    | string
    | undefined;

  const [filterByUserId, setFilterByUserId] = useState(userCookie);
  const [filterByBranchId, setFilterByBranchId] = useState<
    string | undefined | BranchFilterOptionType
  >(branchCookie);

  const service = useUserService();
  const savedLocationsApi = useSavedLocationsApi();
  const [users, setUsers] = useState<undefined | User[]>();
  const [branches, setBranches] = useState<undefined | SavedLocation[]>();

  // The filter is optional: if its lists fail to load, the page shows without
  // it (users stays undefined) instead of waiting on it forever
  useOnce(async function () {
    setLoading(true);
    try {
      const _users = await service.listUsersForCompany();
      const _branches = await savedLocationsApi.getBranches();
      setUsers(_users);
      setBranches(_branches.items);
    } catch (e) {
      console.warn("Error loading the user and branch filter:", { e });
    } finally {
      setLoading(false);
    }
  });

  if (user === undefined) {
    throw new Error("Should not use this if not authenticated");
  }

  if (loading) {
    return { element: () => <></>, usersLoading: true };
  }

  if (users === undefined || branches === undefined) {
    return {
      element: () => <></>,
      usersLoading: false,
      filterByUserId: undefined,
      filterByBranchId: undefined,
    };
  }

  if (users.length <= 1 && branches?.length === 0) {
    // Only one user and no branches - so don't offer the dropdown choice
    return {
      element: () => <></>,
      usersLoading: false,
      filterByUserId: undefined,
      filterByBranchId: undefined,
    };
  }

  return {
    usersLoading: false,
    element: (showUncodedOption: boolean) => (
      <DashboardUserSelect
        type={type}
        user={user}
        users={users || []}
        branches={branches || []}
        selectedUserId={userCookie}
        selectedBranchId={branchCookie}
        onSelectUser={function (value) {
          setFilterByUserId(value);
          setFilterByBranchId(undefined);
          if (value === undefined) {
            removeCookie("fsFilterBookedShipmentsByUserId", {
              domain: cookieDomain(),
            });
          } else {
            setCookie("fsFilterBookedShipmentsByUserId", value, {
              expires: moment().add(1, "year").toDate(),
              domain: cookieDomain(),
              secure: true,
            });
          }
        }}
        onSelectBranch={function (value) {
          setFilterByBranchId(value);
          if (value === undefined) {
            removeCookie("fsFilterBookedShipmentsByBranchId", {
              domain: cookieDomain(),
            });
          } else {
            setCookie("fsFilterBookedShipmentsByBranchId", value, {
              expires: moment().add(1, "year").toDate(),
              domain: cookieDomain(),
              secure: true,
            });
          }
        }}
        showUncodedOption={showUncodedOption}
      />
    ),
    filterByUserId: filterByUserId,
    filterByBranchId: filterByBranchId,
  };
}
